/**
 * One-time migration from legacy localStorage templates to the API.
 * For Media Devoted | Created By Ciro Autiero
 */

import api from '@/services/api';

const LEGACY_PREFIX = 'trafficpulse:csv-export-templates';
const MIGRATED_FLAG = 'trafficpulse:csv-export-templates-migrated:v1';

interface LegacyRow {
  name?: string;
  color?: string;
  tab?: string;
  columns?: unknown;
  isDefault?: boolean;
  downloadBaseName?: string;
}

function parseLegacy(raw: string): LegacyRow[] {
  try {
    const v = JSON.parse(raw) as unknown;
    return Array.isArray(v) ? (v as LegacyRow[]) : [];
  } catch {
    return [];
  }
}

export async function migrateLegacyCsvExportTemplatesIfNeeded(
  userId: string,
): Promise<boolean> {
  if (typeof localStorage === 'undefined') return false;
  if (localStorage.getItem(MIGRATED_FLAG)) return false;

  const raw = localStorage.getItem(`${LEGACY_PREFIX}:${userId}`);
  if (!raw) {
    localStorage.setItem(MIGRATED_FLAG, '1');
    return false;
  }

  const rows = parseLegacy(raw);
  if (rows.length === 0) {
    localStorage.removeItem(`${LEGACY_PREFIX}:${userId}`);
    localStorage.setItem(MIGRATED_FLAG, '1');
    return false;
  }

  let allOk = true;
  for (const row of rows) {
    const tab = typeof row.tab === 'string' ? row.tab : '';
    const cols = Array.isArray(row.columns)
      ? row.columns.filter((c): c is string => typeof c === 'string')
      : [];
    if (!tab || cols.length === 0) continue;

    const color =
      typeof row.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(row.color)
        ? row.color
        : '#3b82f6';
    const name = String(row.name ?? 'Imported').trim().slice(0, 100) || 'Imported';

    try {
      await api.post('/csv-export-templates', {
        tab,
        name,
        color,
        columns: cols,
        isDefault: Boolean(row.isDefault),
        ...(typeof row.downloadBaseName === 'string' && row.downloadBaseName.trim()
          ? { downloadBaseName: row.downloadBaseName.trim().slice(0, 200) }
          : {}),
      });
    } catch {
      allOk = false;
      break;
    }
  }

  if (allOk) {
    localStorage.removeItem(`${LEGACY_PREFIX}:${userId}`);
    localStorage.setItem(MIGRATED_FLAG, '1');
    return true;
  }

  return false;
}
